import React, { useRef } from "react";
import { useAllDoctors } from "../hooks/useDoctors";
import { Link } from "react-router-dom";
import { hospitalNameToSlug } from "../utils/hospitalUtils";
import StarRating from "./StarRating";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";
import SwiperCore from "swiper";
import { FreeMode, Autoplay, Navigation } from "swiper/modules";

SwiperCore.use([FreeMode, Autoplay, Navigation]);

const HospitalDoctorsCarousel = ({ hospitalName }) => {
  const { doctors: allDoctors, loading } = useAllDoctors();
  const swiperRef = useRef(null);
  
  // Use all doctors from the API
  const combinedDoctorsData = allDoctors;
  
  // Remove duplicates based on doctor ID
  const allDoctorsData = combinedDoctorsData.filter((doctor, index, array) => {
    return array.findIndex(d => d.id === doctor.id) === index;
  });
  
  // Exact matching function - only exact hospital name matches
  const isDoctorFromHospital = (doctor, targetHospital) => {
    if (!doctor.hospital || !targetHospital) return false;
    
    const doctorHospital = doctor.hospital.trim();
    const targetHospitalName = targetHospital.trim();
    
    console.log('Exact Matching:', { 
      doctorHospital, 
      targetHospitalName, 
      exactMatch: doctorHospital === targetHospitalName 
    });
    
    // Only exact match
    return doctorHospital === targetHospitalName;
  };
  
  // Filter doctors who work at this specific hospital
  const hospitalDoctors = allDoctorsData.filter(doctor => 
    isDoctorFromHospital(doctor, hospitalName)
  );

  console.log('Hospital Doctors Debug:', {
    hospitalName: `"${hospitalName}"`,
    totalDoctors: allDoctorsData.length,
    exactMatchDoctors: hospitalDoctors.length,
    allDoctorHospitals: allDoctorsData.map(d => `"${d.hospital}"`),
    matchedDoctors: hospitalDoctors.map(d => ({ name: d.name, hospital: `"${d.hospital}"` }))
  });

  // Only show hospital-specific doctors, no fallback
  const doctorsToShow = hospitalDoctors;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-blue-600">Loading doctors...</div>
      </div>
    );
  }

  // If no doctors found, show a message
  if (doctorsToShow.length === 0) {
    return (
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-4xl font-bold text-center mb-12">
          <span className="text-[#1976f2]">Best Doctors</span> from {hospitalName}
        </h2>
        <p className="text-center text-gray-600">Doctor information for {hospitalName} will be available soon. Please check back later.</p>
      </section>
    );
  }

  // Smart duplication logic - only duplicate if we have enough doctors for meaningful looping
  const shouldLoop = doctorsToShow.length >= 3;
  const doctorsForDisplay = shouldLoop ? [...doctorsToShow, ...doctorsToShow] : doctorsToShow;

  return (
    <section className="container mx-auto px-4 pt-16">
      <h2 className="text-4xl font-bold text-center mb-4">
        <span className="text-[#1976f2]">Best Doctors</span> from {hospitalName}
      </h2>
      <p className="text-center text-gray-600 mb-8">Highly qualified medical professionals providing exceptional care</p>

      <div className="overflow-hidden relative">
        <Swiper
          ref={swiperRef}
          slidesPerView={1}
          spaceBetween={16}
          loop={shouldLoop}
          freeMode={true}
          autoplay={shouldLoop ? { delay: 3000, disableOnInteraction: false } : false}
          speed={1000}
          grabCursor={true}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          breakpoints={{
            640: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 24,
            },
          }}
          className="w-full"
        >
          {doctorsForDisplay.map((doctor, index) => (
            <SwiperSlide key={`${doctor.id}-${index}`}>
              <div className="transition-transform duration-300 ease-in-out transform">
                <Link
                  to={`/doctor-details/${doctor.id}`}
                  className="block"
                >
                  <div className="w-full aspect-[4/3] rounded-lg overflow-hidden">
                    <img 
                      src={doctor.image} 
                      alt={doctor.name} 
                      className="w-full h-full object-contain bg-gray-50"
                    />
                  </div>
                  <div className="mt-3 text-center">
                    <h3
                      className="text-2xl font-semibold text-gray-600 truncate"
                      title={doctor.name}
                    >
                      {doctor.name}
                    </h3>
                    {doctor.rating && (
                      <div className="flex justify-center mt-2">
                        <StarRating 
                          rating={parseFloat(doctor.rating)} 
                          size="small" 
                          showRating={true}
                        />
                      </div>
                    )}
                    <p className="text-md text-black-600 mt-1">
                      {doctor.specialty} | {doctor.experience}
                    </p>
                    <p className="text-md text-black-600 mt-1">
                      {doctor.hospital}
                    </p>
                  </div>
                </Link>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      
        {/* Custom Navigation Buttons - Only show if looping is enabled */}
        {shouldLoop && (
          <div className="flex justify-center gap-4 mt-10">
            <button
              onClick={() => swiperRef.current?.swiper.slidePrev()}
              aria-label="Previous"
              className="w-12 h-12 rounded-full flex items-center justify-center bg-[#1976f2] hover:bg-blue-700 text-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => swiperRef.current?.swiper.slideNext()}
              aria-label="Next"
              className="w-12 h-12 rounded-full flex items-center justify-center bg-[#1976f2] hover:bg-blue-700 text-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default HospitalDoctorsCarousel;
